/**
 * SetProfile Calculation Trace Panel (2026-05-08)
 *
 * Read-only debug panel. Wraps the
 * `/api/tt/admin/setprofile-debug-trace` endpoint and renders six
 * sections + invariants. Used to audit the v2 SetProfile pipeline
 * before touching any logic / thresholds.
 *
 * Pure presentational. No write operations.
 */
import React, { useState, useEffect, useRef } from 'react';
import { adminGet } from '../data/api';
import { Bug, AlertTriangle, CheckCircle2 } from 'lucide-react';

const fmt = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return v.toFixed(4).replace(/\.?0+$/, '');
  return String(v);
};

export default function SetProfileDebugTracePanel({ pin }) {
  const [matchId, setMatchId] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  const load = async (mid) => {
    const target = (mid ?? matchId).trim();
    if (!target) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await adminGet(
        `/tt/admin/setprofile-debug-trace?match_id=${encodeURIComponent(target)}`,
        pin || '',
      );
      setData(r);
    } catch (e) {
      setErr(e?.message || 'load failed');
    } finally {
      setLoading(false);
    }
  };

  // Listen for global "load this match in the trace panel" events
  // dispatched from the RECENT PERSISTED PREDICTIONS table (or any
  // other place that wants to drive the panel without prop drilling).
  useEffect(() => {
    const onLoad = (e) => {
      const mid = e?.detail?.matchId;
      if (!mid) return;
      setMatchId(mid);
      // Scroll into view + flash highlight, then trigger load.
      try {
        containerRef.current?.scrollIntoView({
          behavior: 'smooth', block: 'start',
        });
      } catch (_) { /* noop */ }
      load(mid);
    };
    window.addEventListener('pointedge:setprofile-trace-load', onLoad);
    return () =>
      window.removeEventListener('pointedge:setprofile-trace-load', onLoad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  return (
    <div
      ref={containerRef}
      data-testid="setprofile-debug-trace-panel"
      className="rounded-sm border border-zinc-800 bg-zinc-950/40 p-3 text-[11px] space-y-3"
      style={{ fontFamily: 'monospace' }}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Bug size={13} className="text-amber-400" />
        <span className="text-zinc-100 font-semibold tracking-wide">
          SETPROFILE CALCULATION TRACE
        </span>
        <span className="text-[9px] px-1.5 py-0.5 rounded-sm border border-zinc-700 text-zinc-400">
          read-only · audit
        </span>
      </div>

      <div className="flex gap-2">
        <input
          data-testid="trace-match-id-input"
          type="text"
          value={matchId}
          onChange={(e) => setMatchId(e.target.value)}
          placeholder="match_id (e.g. ttm_player1_player2_…)"
          className="flex-1 bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-1 text-zinc-100 text-[10px]"
        />
        <button
          data-testid="trace-load-btn"
          onClick={() => load()}
          disabled={loading || !matchId.trim()}
          className="px-3 py-1 rounded-sm bg-amber-900/40 border border-amber-700 text-amber-200 hover:bg-amber-900/60 disabled:opacity-50"
        >
          {loading ? 'loading…' : 'trace'}
        </button>
      </div>

      {err && (
        <div className="text-rose-300 px-2 py-1 rounded-sm bg-rose-950/40 border border-rose-800">
          {err}
        </div>
      )}

      {data && data.error && (
        <div className="text-amber-300 px-2 py-1 rounded-sm bg-amber-950/40 border border-amber-800">
          {data.error}
        </div>
      )}

      {data && !data.error && (
        <div className="space-y-3">
          {/* INVARIANTS — show first because that's the alert layer */}
          <Section title="INVARIANTS" testid="trace-invariants">
            <div className="space-y-1">
              {(data.invariants || []).map((inv) => (
                <div
                  key={inv.code}
                  data-testid={`invariant-${inv.code}`}
                  className={`flex items-start gap-1.5 px-2 py-1 rounded-sm border ${
                    inv.passed
                      ? 'bg-emerald-950/20 border-emerald-900/50 text-emerald-200'
                      : 'bg-rose-950/30 border-rose-800 text-rose-200'
                  }`}
                >
                  {inv.passed ? (
                    <CheckCircle2 size={11} className="mt-0.5 shrink-0" />
                  ) : (
                    <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                  )}
                  <div className="flex-1">
                    <div className="font-semibold">{inv.code}</div>
                    <div className="text-[10px] opacity-90">{inv.description}</div>
                    {!inv.passed && (
                      <div className="text-[10px] opacity-70 mt-0.5">
                        {Object.entries(inv)
                          .filter(([k]) =>
                            !['code', 'description', 'passed', 'note'].includes(k),
                          )
                          .map(([k, v]) => (
                            <span key={k} className="mr-2">
                              {k}=<b>{fmt(v)}</b>
                            </span>
                          ))}
                      </div>
                    )}
                    {inv.note && (
                      <div className="text-[9px] opacity-60 mt-0.5">
                        ↳ {inv.note}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* RAW INPUTS */}
          <Section title="A · RAW INPUTS" testid="trace-raw-inputs">
            <KeyValueGrid obj={data.raw_inputs} excludeKeys={['scoreline']} />
            <div className="mt-2">
              <div className="text-[9px] tracking-wider text-zinc-500 mb-1">
                scoreline cells (sum check = {fmt(data.raw_inputs?.scoreline?.sum_check)})
              </div>
              <KeyValueGrid obj={data.raw_inputs?.scoreline} />
            </div>
          </Section>

          {/* DERIVED METRICS */}
          <Section title="B · DERIVED METRICS" testid="trace-derived">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-zinc-500">
                  <th className="text-left">metric</th>
                  <th className="text-left">formula</th>
                  <th className="text-right">value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.derived_metrics || {}).map(([k, v]) => (
                  <tr key={k} className="border-t border-zinc-900 text-zinc-300">
                    <td className="py-1 font-semibold">{k}</td>
                    <td className="py-1 text-zinc-400 text-[9px]">
                      {v.formula}
                      {v.definition_note && (
                        <div className="text-[9px] text-amber-300 mt-0.5">
                          ↳ {v.definition_note}
                        </div>
                      )}
                    </td>
                    <td className="py-1 text-right text-emerald-300 font-bold">
                      {fmt(v.raw_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* PROFILE CLASSIFICATION */}
          <Section title="C · PROFILE CLASSIFICATION" testid="trace-classification">
            <div className="text-zinc-200 mb-1">
              <span className="text-zinc-500">winner:</span>{' '}
              <b className="text-emerald-300">
                {data.profile_classification?.winner}
              </b>
              <span className="ml-3 text-zinc-500">confidence:</span>{' '}
              <b>{fmt(data.profile_classification?.final_confidence)}</b>
              {data.profile_classification?.second_best_profile && (
                <>
                  <span className="ml-3 text-zinc-500">2nd:</span>{' '}
                  <b className="text-zinc-400">
                    {data.profile_classification.second_best_profile}
                  </b>
                </>
              )}
            </div>
            <div className="space-y-1">
              {(data.profile_classification?.candidates_considered || []).map(
                (c) => (
                  <div
                    key={c.archetype}
                    className={`text-[10px] ${
                      c.passed ? 'text-emerald-300' : 'text-zinc-500'
                    }`}
                  >
                    {c.passed ? '✓' : '·'} <b>{c.archetype}</b>{' '}
                    <span className="text-[9px] opacity-70">
                      [
                      {c.conditions
                        .map((cond) => (cond.passed ? '✓' : '✗') + ' ' + cond.check)
                        .join(' · ')}
                      ]
                    </span>
                  </div>
                ),
              )}
            </div>
            <div className="text-[9px] text-zinc-500 mt-1">
              confidence formula: {data.profile_classification?.confidence_formula}
            </div>
          </Section>

          {/* MARKET SCORE TRACE */}
          <Section title="D · MARKET SCORES" testid="trace-market">
            <div className="text-[9px] text-zinc-500 mb-1 italic">
              EV cons uses p_lo80 (used by eligibility / final_score).
              EV raw uses p_calibrated (matches the main UI O/U & HC tables).
              The two diverge whenever p_lo80 &lt; p_calibrated.
            </div>
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-zinc-500">
                  <th className="text-left">market</th>
                  <th className="text-right">base</th>
                  <th
                    className="text-right"
                    title="Probability used for the conservative EV (p_lo80 if available)"
                  >
                    prob (p_lo80)
                  </th>
                  <th
                    className="text-right"
                    title="Probability used for the raw EV (p_calibrated)"
                  >
                    prob (p_raw)
                  </th>
                  <th className="text-right">odds</th>
                  <th className="text-right" title="Line / side used for HC and OU">
                    side / line
                  </th>
                  <th
                    className="text-right"
                    title="Conservative EV: prob_used × odds − 1. Used for eligibility / final_score."
                  >
                    EV cons %
                  </th>
                  <th
                    className="text-right"
                    title="Raw EV: prob_raw × odds − 1. Matches the main UI O/U & HC EV column."
                  >
                    EV raw %
                  </th>
                  <th className="text-right">ev_q</th>
                  <th className="text-right">final</th>
                  <th className="text-left pl-2">eligible / blocked</th>
                </tr>
              </thead>
              <tbody>
                {(data.market_score_trace || []).map((r) => (
                  <tr
                    key={r.market}
                    data-testid={`trace-market-row-${r.market}`}
                    className={`border-t border-zinc-900 ${
                      r.eligible ? 'text-zinc-200' : 'text-zinc-500'
                    }`}
                  >
                    <td className="py-1 font-semibold">{r.market}</td>
                    <td className="text-right">{fmt(r.base_score)}</td>
                    <td className="text-right">{fmt(r.probability_used)}</td>
                    <td className="text-right">{fmt(r.probability_raw)}</td>
                    <td className="text-right">{fmt(r.odds)}</td>
                    <td className="text-right text-zinc-500 text-[9px]">
                      {r.side || '—'}
                      {r.line != null ? ` @ ${r.line}` : ''}
                    </td>
                    <td
                      className={`text-right ${
                        r.ev_pct == null
                          ? ''
                          : r.ev_pct > 0
                          ? 'text-emerald-300'
                          : 'text-rose-300'
                      }`}
                    >
                      {fmt(r.ev_pct)}
                    </td>
                    <td
                      className={`text-right ${
                        r.ev_pct_raw == null
                          ? ''
                          : r.ev_pct_raw > 0
                          ? 'text-emerald-200/80'
                          : 'text-rose-200/80'
                      }`}
                      data-testid={`trace-market-ev-raw-${r.market}`}
                    >
                      {fmt(r.ev_pct_raw)}
                    </td>
                    <td className="text-right">{fmt(r.ev_quality)}</td>
                    <td className="text-right text-emerald-300 font-bold">
                      {fmt(r.final_score)}
                    </td>
                    <td className="pl-2 text-[9px]">
                      {r.eligible ? (
                        <span className="text-emerald-300">eligible</span>
                      ) : (
                        <span className="text-rose-300">{r.blocked_reason}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-1 text-[9px] text-zinc-600 italic">
              EV formula: EV = prob × odds − 1 · final_score = base × confidence × ev_quality
            </div>
            {(data.market_score_trace || []).some(r => r.book_snapshot_at) && (
              <div className="mt-1 text-[9px] text-zinc-500" data-testid="trace-market-snapshots">
                odds source / timestamp:{' '}
                {(data.market_score_trace || [])
                  .filter(r => r.book_snapshot_at)
                  .map(r => `${r.market}: ${String(r.book_snapshot_at).slice(0,19)}`)
                  .join(' · ')}
              </div>
            )}
          </Section>

          {/* OU LINE TRACE */}
          <Section title="E · O/U LINE TRACE" testid="trace-ou-line">
            {data.ou_line_trace?.edge_verdict && (
              <div
                data-testid="trace-ou-edge-verdict"
                className="mb-2 px-2 py-1 rounded-sm text-[10px] flex items-start gap-2"
                style={{
                  background:
                    data.ou_line_trace.edge_verdict === 'no_edge' ? 'rgba(168,85,247,0.10)'
                    : data.ou_line_trace.edge_verdict === 'strong' ? 'rgba(16,185,129,0.12)'
                    : data.ou_line_trace.edge_verdict === 'mild' ? 'rgba(245,158,11,0.10)'
                    : 'rgba(113,113,122,0.10)',
                  border:
                    data.ou_line_trace.edge_verdict === 'no_edge' ? '1px solid rgba(168,85,247,0.35)'
                    : data.ou_line_trace.edge_verdict === 'strong' ? '1px solid rgba(16,185,129,0.40)'
                    : data.ou_line_trace.edge_verdict === 'mild' ? '1px solid rgba(245,158,11,0.35)'
                    : '1px solid rgba(113,113,122,0.30)',
                  color:
                    data.ou_line_trace.edge_verdict === 'no_edge' ? '#DDD6FE'
                    : data.ou_line_trace.edge_verdict === 'strong' ? '#A7F3D0'
                    : data.ou_line_trace.edge_verdict === 'mild' ? '#FCD34D'
                    : '#A1A1AA',
                }}
              >
                <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-semibold tracking-wider flex-shrink-0"
                      style={{
                        background:
                          data.ou_line_trace.edge_verdict === 'no_edge' ? 'rgba(168,85,247,0.25)'
                          : data.ou_line_trace.edge_verdict === 'strong' ? 'rgba(16,185,129,0.25)'
                          : data.ou_line_trace.edge_verdict === 'mild' ? 'rgba(245,158,11,0.25)'
                          : 'rgba(113,113,122,0.25)',
                      }}>
                  edge: {data.ou_line_trace.edge_verdict.toUpperCase()}
                </span>
                <span>{data.ou_line_trace.edge_reason || '—'}</span>
              </div>
            )}
            <KeyValueGrid
              obj={data.ou_line_trace}
              excludeKeys={[
                'engine_curve_at_75_5',
                'engine_curve_at_77_5',
                'engine_curve_at_74_5',
                'engine_curve_at_selected',
                'available_book_lines',
                'edge_verdict',
                'edge_reason',
                'point_density',
                'selected_ou_ev',
              ]}
            />

            {/* 2026-05-08 — Point-density audit layer.
                3-1 alone does not decide Over vs Under: density
                (points/set) decides. The block below answers
                "given E[sets] and E[total], are there enough
                points/set to clear the line?". */}
            {data.ou_line_trace?.point_density && (() => {
              const pd = data.ou_line_trace.point_density;
              const verdict = pd.density_verdict || 'UNKNOWN';
              const colorMap = {
                LOW: { bg: 'rgba(244,63,94,0.10)', border: 'rgba(244,63,94,0.40)',
                       fg: '#FECACA', pill: 'rgba(244,63,94,0.30)' },
                NEUTRAL: { bg: 'rgba(113,113,122,0.10)', border: 'rgba(113,113,122,0.30)',
                            fg: '#D4D4D8', pill: 'rgba(113,113,122,0.30)' },
                GOOD: { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.35)',
                         fg: '#FCD34D', pill: 'rgba(245,158,11,0.30)' },
                STRONG: { bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.40)',
                           fg: '#A7F3D0', pill: 'rgba(16,185,129,0.30)' },
                UNKNOWN: { bg: 'rgba(63,63,70,0.10)', border: 'rgba(63,63,70,0.40)',
                            fg: '#A1A1AA', pill: 'rgba(63,63,70,0.30)' },
              };
              const colors = colorMap[verdict] || colorMap.UNKNOWN;
              return (
                <div
                  data-testid="trace-point-density"
                  className="mt-2 px-2 py-1.5 rounded-sm text-[10px]"
                  style={{
                    background: colors.bg,
                    border: `1px solid ${colors.border}`,
                    color: colors.fg,
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="px-1.5 py-0.5 rounded-sm text-[9px] font-semibold tracking-wider"
                      style={{ background: colors.pill }}
                      data-testid="point-density-verdict-badge"
                    >
                      DENSITY: {verdict}
                    </span>
                    <span className="text-zinc-300 text-[10px]">
                      Point-density audit (3-1 alone ≠ Over)
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-0.5 text-zinc-300">
                    <div>
                      <span className="text-zinc-500">expected_sets:</span>{' '}
                      <b data-testid="pd-expected-sets">{fmt(pd.expected_number_of_sets)}</b>
                    </div>
                    <div>
                      <span className="text-zinc-500">expected_pts/set:</span>{' '}
                      <b data-testid="pd-expected-pps">{fmt(pd.expected_points_per_set)}</b>
                    </div>
                    <div>
                      <span className="text-zinc-500">required_pts/set @ {pd.selected_ou_line ?? '—'}:</span>{' '}
                      <b data-testid="pd-required-pps">
                        {fmt(pd.required_points_per_set_for_selected_line)}
                      </b>
                    </div>
                    <div>
                      <span className="text-zinc-500">density_edge:</span>{' '}
                      <b
                        data-testid="pd-density-edge"
                        className={
                          pd.point_density_edge == null ? ''
                            : pd.point_density_edge >= 0.5 ? 'text-emerald-300'
                            : pd.point_density_edge <= -0.5 ? 'text-rose-300'
                            : 'text-zinc-200'
                        }
                      >
                        {pd.point_density_edge == null ? '—'
                          : `${pd.point_density_edge >= 0 ? '+' : ''}${Number(pd.point_density_edge).toFixed(2)}`}
                      </b>
                    </div>
                    <div>
                      <span className="text-zinc-500">blowout_risk (3-0):</span>{' '}
                      <b data-testid="pd-blowout">
                        {pd.blowout_risk == null ? '—'
                          : `${(pd.blowout_risk * 100).toFixed(1)}%`}
                      </b>
                    </div>
                    <div>
                      <span className="text-zinc-500">P(3-1):</span>{' '}
                      <b>
                        {pd.four_set_probability == null ? '—'
                          : `${(pd.four_set_probability * 100).toFixed(1)}%`}
                      </b>
                    </div>
                    <div>
                      <span className="text-zinc-500">P(3-2):</span>{' '}
                      <b>
                        {pd.five_set_probability == null ? '—'
                          : `${(pd.five_set_probability * 100).toFixed(1)}%`}
                      </b>
                    </div>
                    <div>
                      <span className="text-zinc-500">expected_total:</span>{' '}
                      <b>{fmt(pd.expected_total_points)}</b>
                    </div>
                    {pd.close_set_probability != null && (
                      <div>
                        <span className="text-zinc-500">close_set_prob:</span>{' '}
                        <b>{(pd.close_set_probability * 100).toFixed(1)}%</b>
                      </div>
                    )}
                    {pd.deuce_probability != null && (
                      <div>
                        <span className="text-zinc-500">deuce_prob:</span>{' '}
                        <b>{(pd.deuce_probability * 100).toFixed(1)}%</b>
                      </div>
                    )}
                    {pd.average_set_margin != null && (
                      <div>
                        <span className="text-zinc-500">avg_set_margin:</span>{' '}
                        <b>{fmt(pd.average_set_margin)}</b>
                      </div>
                    )}
                  </div>
                  {pd.density_reason && (
                    <div className="mt-1 text-[9px] leading-snug">
                      ↳ {pd.density_reason}
                    </div>
                  )}
                  <div className="mt-1 text-[9px] text-zinc-600 italic">
                    Bands: LOW &lt; -0.50 · NEUTRAL [-0.50, +0.50) ·
                    GOOD [+0.50, +1.50) · STRONG ≥ +1.50 (pts/set)
                  </div>
                </div>
              );
            })()}
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-1 text-[9px]">
              {[
                ['74.5', data.ou_line_trace?.engine_curve_at_74_5],
                ['75.5', data.ou_line_trace?.engine_curve_at_75_5],
                ['77.5', data.ou_line_trace?.engine_curve_at_77_5],
                ['selected', data.ou_line_trace?.engine_curve_at_selected],
              ].map(([label, c]) => {
                const isSelected = label === 'selected';
                const sourceLabel = c?.source === 'engine_curve_exact' ? 'exact'
                  : c?.source === 'engine_curve_interpolated' ? 'interp'
                  : c?.source && c.source.startsWith('engine_curve_interpolated') ? 'interp'
                  : c?.source === 'normal_cdf_approx' ? 'norm-cdf'
                  : c?.source === 'unavailable' ? '—'
                  : null;
                return (
                  <div
                    key={label}
                    data-testid={`ou-curve-${label === 'selected' ? 'selected' : label.replace('.', '_')}`}
                    className={`bg-zinc-900/60 border rounded-sm p-1 ${
                      isSelected ? 'border-amber-700/60' : 'border-zinc-800'
                    }`}
                  >
                    <div className="text-zinc-500 flex items-center justify-between gap-1">
                      <span>@ {label}{c?.line != null && label === 'selected'
                        ? ` (${c.line})` : ''}</span>
                      {sourceLabel && isSelected && (
                        <span
                          className="text-[8px] px-1 py-0 rounded-sm bg-amber-900/40 text-amber-200"
                          data-testid="ou-selected-source-pill"
                          title={`source = ${c.source}`}
                        >
                          {sourceLabel}
                        </span>
                      )}
                    </div>
                    <div className="text-emerald-300">
                      Over {fmt(c?.over_prob)}
                    </div>
                    <div className="text-violet-300">
                      Under {fmt(c?.under_prob)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 2026-05-09 — odds + raw EV at the SELECTED line. */}
            {data.ou_line_trace?.selected_ou_ev && (() => {
              const ev = data.ou_line_trace.selected_ou_ev;
              const sel = data.ou_line_trace.engine_curve_at_selected || {};
              const haveOdds = ev.over_odd != null || ev.under_odd != null;
              return (
                <div
                  data-testid="ou-selected-ev-block"
                  className="mt-1 px-2 py-1.5 rounded-sm text-[10px] border border-zinc-800 bg-zinc-950/40"
                >
                  <div className="text-[10px] text-zinc-400 font-semibold mb-1">
                    @ selected {data.ou_line_trace.selected_ou_line ?? '—'} · book EV (raw)
                  </div>
                  {haveOdds ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-0.5 text-zinc-300">
                      <div>
                        <span className="text-zinc-500">Over prob:</span>{' '}
                        <b className="text-emerald-300">{fmt(sel.over_prob)}</b>
                      </div>
                      <div>
                        <span className="text-zinc-500">Over odds:</span>{' '}
                        <b>{ev.over_odd == null ? '—' : Number(ev.over_odd).toFixed(2)}</b>
                      </div>
                      <div>
                        <span className="text-zinc-500">EV raw Over:</span>{' '}
                        <b
                          className={
                            ev.ev_over_pct_raw == null ? 'text-zinc-500'
                              : ev.ev_over_pct_raw > 0 ? 'text-emerald-300'
                              : 'text-rose-300'
                          }
                          data-testid="ou-selected-ev-over"
                        >
                          {ev.ev_over_pct_raw == null ? '—'
                            : `${ev.ev_over_pct_raw >= 0 ? '+' : ''}${Number(ev.ev_over_pct_raw).toFixed(2)}%`}
                        </b>
                      </div>
                      <div>
                        <span className="text-zinc-500">bookmaker:</span>{' '}
                        <b>{ev.bookmaker || '—'}</b>
                      </div>
                      <div>
                        <span className="text-zinc-500">Under prob:</span>{' '}
                        <b className="text-violet-300">{fmt(sel.under_prob)}</b>
                      </div>
                      <div>
                        <span className="text-zinc-500">Under odds:</span>{' '}
                        <b>{ev.under_odd == null ? '—' : Number(ev.under_odd).toFixed(2)}</b>
                      </div>
                      <div>
                        <span className="text-zinc-500">EV raw Under:</span>{' '}
                        <b
                          className={
                            ev.ev_under_pct_raw == null ? 'text-zinc-500'
                              : ev.ev_under_pct_raw > 0 ? 'text-emerald-300'
                              : 'text-rose-300'
                          }
                          data-testid="ou-selected-ev-under"
                        >
                          {ev.ev_under_pct_raw == null ? '—'
                            : `${ev.ev_under_pct_raw >= 0 ? '+' : ''}${Number(ev.ev_under_pct_raw).toFixed(2)}%`}
                        </b>
                      </div>
                      {ev.book_snapshot_at && (
                        <div>
                          <span className="text-zinc-500">odds source:</span>{' '}
                          <span title={ev.book_snapshot_at}>
                            {String(ev.book_snapshot_at).slice(11, 19)}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-zinc-500 text-[10px]">
                      no book odds at line {data.ou_line_trace.selected_ou_line ?? '—'} —
                      probabilities {sel.source && sel.source !== 'engine_curve_exact'
                        ? `(${sel.source})` : ''} shown above
                    </div>
                  )}
                  <div className="mt-1 text-[9px] text-zinc-600 italic">
                    {ev.ev_formula || 'EV = prob × odds − 1 (raw uses p_calibrated)'}
                  </div>
                </div>
              );
            })()}
          </Section>

          {/* FINAL RECOMMENDATION */}
          <Section title="F · FINAL RECOMMENDATION" testid="trace-final-rec">
            <KeyValueGrid
              obj={data.final_recommendation_trace}
              excludeKeys={['blocked_markets']}
            />
            {(data.final_recommendation_trace?.blocked_markets || []).length > 0 && (
              <div className="mt-1">
                <div className="text-[9px] text-zinc-500">blocked markets:</div>
                <ul className="text-[9px] text-rose-300">
                  {data.final_recommendation_trace.blocked_markets.map((b) => (
                    <li key={b.market}>
                      • <b>{b.market}</b>: {b.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>

          {/* G · SHADOW MARKET SELECTION MATRIX (Phase 1 audit-only) */}
          {data.shadow_market_selection && (
            <Section
              title="G · SHADOW MARKET SELECTION (Phase 1 — audit only)"
              testid="trace-shadow"
            >
              <div className="text-[9px] text-zinc-500 mb-2">
                Engine: {data.shadow_market_selection.engine_version}
                {' · '}weights: structural ×0.80 + archetype_prior ×0.20
                {' · '}low_confidence_active:{' '}
                <b className={data.shadow_market_selection.low_confidence_active
                  ? 'text-rose-300' : 'text-zinc-300'}>
                  {String(!!data.shadow_market_selection.low_confidence_active)}
                </b>
                {data.shadow_market_selection.ev_thresholds && (
                  <span className="ml-2" data-testid="shadow-ev-thresholds">
                    {' · '}EV tiers: ≥{data.shadow_market_selection.ev_thresholds.min_eligible_pct}% eligible /
                    [{data.shadow_market_selection.ev_thresholds.min_recommend_pct}%–{data.shadow_market_selection.ev_thresholds.min_eligible_pct}%) scout /
                    &lt;{data.shadow_market_selection.ev_thresholds.min_recommend_pct}% no-recommend
                  </span>
                )}
              </div>

              {data.shadow_market_selection.ev_tier && (
                <div
                  data-testid="shadow-ev-tier"
                  className="mb-2 px-2 py-1 rounded-sm text-[10px] flex items-center gap-2"
                  style={{
                    background:
                      data.shadow_market_selection.ev_tier === 'eligible' ? 'rgba(16,185,129,0.10)'
                      : data.shadow_market_selection.ev_tier === 'scout_only_low_ev' ? 'rgba(245,158,11,0.10)'
                      : data.shadow_market_selection.ev_tier === 'below_recommend' ? 'rgba(244,63,94,0.10)'
                      : 'rgba(113,113,122,0.10)',
                    border:
                      data.shadow_market_selection.ev_tier === 'eligible' ? '1px solid rgba(16,185,129,0.30)'
                      : data.shadow_market_selection.ev_tier === 'scout_only_low_ev' ? '1px solid rgba(245,158,11,0.30)'
                      : data.shadow_market_selection.ev_tier === 'below_recommend' ? '1px solid rgba(244,63,94,0.30)'
                      : '1px solid rgba(113,113,122,0.30)',
                    color:
                      data.shadow_market_selection.ev_tier === 'eligible' ? '#A7F3D0'
                      : data.shadow_market_selection.ev_tier === 'scout_only_low_ev' ? '#FCD34D'
                      : data.shadow_market_selection.ev_tier === 'below_recommend' ? '#FECACA'
                      : '#A1A1AA',
                  }}
                >
                  <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-semibold tracking-wider">
                    EV TIER: {data.shadow_market_selection.ev_tier.toUpperCase()}
                  </span>
                  {data.shadow_market_selection.ev_below_recommend_threshold && (
                    <span>
                      Top market EV is below the recommend floor — shadow recommendation forced to <b>None</b>.
                    </span>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-2">
                  <div className="text-[9px] text-zinc-500">LEGACY</div>
                  <div className="text-zinc-200 font-bold">
                    {data.shadow_market_selection.legacy_recommended_market || '—'}
                  </div>
                </div>
                <div className="bg-zinc-900/60 border border-amber-800 rounded-sm p-2">
                  <div className="text-[9px] text-amber-400">SHADOW</div>
                  <div className="text-amber-200 font-bold">
                    {data.shadow_market_selection.shadow_recommended_market || '—'}
                    {data.shadow_market_selection.scout_only && (
                      <span className="ml-1 text-[9px] px-1 py-0.5 rounded-sm bg-violet-900/40 border border-violet-700 text-violet-200">
                        scout_only
                      </span>
                    )}
                  </div>
                </div>
                <div
                  className={`rounded-sm p-2 border ${
                    data.shadow_market_selection.agreement_with_legacy === false
                      ? 'bg-rose-950/30 border-rose-800'
                      : 'bg-emerald-950/20 border-emerald-900/50'
                  }`}
                >
                  <div className="text-[9px] text-zinc-500">AGREEMENT</div>
                  <div
                    className={
                      data.shadow_market_selection.agreement_with_legacy === false
                        ? 'text-rose-200 font-bold'
                        : 'text-emerald-200 font-bold'
                    }
                  >
                    {data.shadow_market_selection.agreement_with_legacy === null
                      ? 'n/a'
                      : data.shadow_market_selection.agreement_with_legacy
                      ? 'agree'
                      : 'disagree'}
                  </div>
                </div>
              </div>

              {data.shadow_market_selection.disagreement_reason && (
                <div className="text-[10px] text-rose-200 bg-rose-950/30 border border-rose-800 px-2 py-1 rounded-sm mb-2">
                  ↳ {data.shadow_market_selection.disagreement_reason}
                </div>
              )}
              {(data.shadow_market_selection.scout_reasons || []).length > 0 && (
                <div className="text-[10px] text-violet-200 bg-violet-950/30 border border-violet-800 px-2 py-1 rounded-sm mb-2">
                  scout_reasons: {data.shadow_market_selection.scout_reasons.join(' · ')}
                </div>
              )}

              <table className="w-full text-[10px]">
                <thead>
                  <tr className="text-zinc-500">
                    <th className="text-left">market</th>
                    <th className="text-right">struct</th>
                    <th className="text-right">prior</th>
                    <th className="text-right">EV%</th>
                    <th className="text-right">ev_q</th>
                    <th className="text-right">conf_f</th>
                    <th className="text-right">guard</th>
                    <th className="text-right">final</th>
                    <th className="text-right">rank</th>
                    <th className="text-left pl-2">block</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.shadow_market_selection.matrix || []).map((r) => (
                    <tr
                      key={r.market}
                      className={`border-t border-zinc-900 ${
                        r.guard_factor === 0
                          ? 'text-zinc-600'
                          : r.market === data.shadow_market_selection.shadow_recommended_market
                          ? 'text-amber-200 font-bold'
                          : 'text-zinc-300'
                      }`}
                    >
                      <td>{r.market}</td>
                      <td className="text-right">{fmt(r.structural_score)}</td>
                      <td className="text-right">{fmt(r.archetype_prior)}</td>
                      <td className="text-right">{fmt(r.ev_pct)}</td>
                      <td className="text-right">{fmt(r.ev_quality)}</td>
                      <td className="text-right">{fmt(r.confidence_factor)}</td>
                      <td className="text-right">{fmt(r.guard_factor)}</td>
                      <td className="text-right text-emerald-300">{fmt(r.final_score)}</td>
                      <td className="text-right">{r.rank}</td>
                      <td className="pl-2 text-[9px] text-rose-300">
                        {r.blocked_reason || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Structural breakdowns */}
              {data.shadow_market_selection.structural_scores && (
                <details className="mt-2">
                  <summary className="text-[10px] text-zinc-400 cursor-pointer">
                    structural breakdowns (mw / hc_fav / hc_dog / over / under)
                  </summary>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                    {['mw', 'hc_fav', 'hc_dog', 'over', 'under'].map((k) => {
                      const s = data.shadow_market_selection.structural_scores[k];
                      if (!s) return null;
                      return (
                        <div
                          key={k}
                          className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-1.5"
                        >
                          <div className="text-[9px] text-zinc-500">
                            {k} → score <b className="text-emerald-300">{fmt(s.score)}</b>
                          </div>
                          <div className="text-[8px] text-zinc-400 italic mb-1">
                            {s.formula}
                          </div>
                          <KeyValueGrid obj={s.components} />
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}
            </Section>
          )}

          {/* H · MARKET EV AUDIT — explain selector EV vs O/U Trace EV (2026-05-20) */}
          {data.market_ev_audit && data.market_ev_audit.by_market && (
            <Section
              title="H · MARKET EV AUDIT (Phase 1 — audit only)"
              testid="trace-market-ev-audit"
            >
              <div className="text-[9px] text-zinc-500 mb-2 italic">
                {data.market_ev_audit.explanation}
              </div>
              {(data.market_ev_audit.integrity_flags || []).length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1">
                  {data.market_ev_audit.integrity_flags.map((f) => (
                    <span
                      key={f}
                      data-testid={`mea-flag-${f}`}
                      className="px-1.5 py-0.5 rounded-sm text-[9px] font-semibold bg-rose-900/40 text-rose-100 border border-rose-700"
                    >
                      ⚠ {f}
                    </span>
                  ))}
                </div>
              )}
              <div className="space-y-1.5">
                {Object.entries(data.market_ev_audit.by_market).map(([market, m]) => {
                  if (!m) return null;
                  const flagged = m.block_reason === 'ev_not_positive'
                    && m.ou_trace_ev_raw != null && m.ou_trace_ev_raw > 5;
                  const palette = flagged
                    ? 'bg-rose-950/30 border-rose-800 text-rose-100'
                    : m.block_reason
                    ? 'bg-zinc-900/40 border-zinc-800 text-zinc-300'
                    : 'bg-emerald-950/20 border-emerald-900 text-emerald-100';
                  return (
                    <div
                      key={market}
                      data-testid={`mea-row-${market}`}
                      className={`rounded-sm px-2 py-1.5 text-[10px] border ${palette}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-bold tracking-wider">{market.toUpperCase()}</span>
                        {m.side && (
                          <span className="text-[9px] text-zinc-500">· side {m.side}</span>
                        )}
                        {m.selected_line != null && (
                          <span className="text-[9px] text-zinc-500">· line {m.selected_line}</span>
                        )}
                        {m.block_reason && (
                          <span className="ml-auto px-1.5 py-0.5 rounded-sm text-[9px] font-semibold bg-rose-900/40 text-rose-100 border border-rose-700">
                            blocked: {m.block_reason}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-0.5 text-[9px]">
                        <span data-testid={`mea-${market}-selector-ev`}>
                          selector EV: <b className={(m.selector_ev_used ?? 0) > 0 ? 'text-emerald-300' : 'text-rose-300'}>{m.selector_ev_used != null ? m.selector_ev_used + '%' : '—'}</b>
                        </span>
                        <span data-testid={`mea-${market}-trace-ev`}>
                          O/U Trace EV (raw): <b className={(m.ou_trace_ev_raw ?? 0) > 0 ? 'text-emerald-300' : 'text-rose-300'}>{m.ou_trace_ev_raw != null ? m.ou_trace_ev_raw + '%' : '—'}</b>
                        </span>
                        <span>Δ trace-vs-selector: <b className="text-amber-200">{m.ev_delta_trace_vs_selector != null ? (m.ev_delta_trace_vs_selector > 0 ? '+' : '') + m.ev_delta_trace_vs_selector + 'pp' : '—'}</b></span>
                        <span>shrinkage: <b className="text-amber-200">{m.probability_shrinkage_pp != null ? (m.probability_shrinkage_pp > 0 ? '+' : '') + m.probability_shrinkage_pp + 'pp' : '—'}</b></span>
                        <span>selector prob (used): <b className="text-zinc-200">{m.selector_probability_used != null ? (m.selector_probability_used * 100).toFixed(2) + '%' : '—'}</b></span>
                        <span>OU Trace prob: <b className="text-zinc-200">{m.ou_trace_probability != null ? (m.ou_trace_probability * 100).toFixed(2) + '%' : '—'}</b></span>
                        <span>prob raw (cal): <b className="text-zinc-200">{m.probability_raw != null ? (m.probability_raw * 100).toFixed(2) + '%' : '—'}</b></span>
                        <span>source: <b className="text-zinc-200">{m.probability_source_used || '—'}</b></span>
                        <span>odds: <b className="text-zinc-200">{m.odds_used ?? '—'}</b></span>
                        <span>break-even @ odds: <b className="text-zinc-200">{m.p_break_even_at_odds_pct != null ? m.p_break_even_at_odds_pct + '%' : '—'}</b></span>
                        <span>margin vs BE: <b className={(m.margin_vs_break_even_pp ?? 0) > 0 ? 'text-emerald-300' : 'text-rose-300'}>{m.margin_vs_break_even_pp != null ? (m.margin_vs_break_even_pp > 0 ? '+' : '') + m.margin_vs_break_even_pp + 'pp' : '—'}</b></span>
                        <span>fair odds (raw): <b className="text-zinc-200">{m.fair_odds_raw ?? '—'}</b></span>
                        <span>fair odds (selector): <b className="text-zinc-200">{m.fair_odds_selector ?? '—'}</b></span>
                        {(market === 'over' || market === 'under') && (
                          <>
                            <span>same line as OU Trace? <b className={m.same_line_used === true ? 'text-emerald-300' : m.same_line_used === false ? 'text-rose-300' : 'text-zinc-400'}>{m.same_line_used === true ? 'yes' : m.same_line_used === false ? `NO (selector ${m.selected_line} vs trace ${m.ou_trace_line})` : '—'}</b></span>
                            <span>same odds as OU Trace? <b className={m.same_odds_used === true ? 'text-emerald-300' : m.same_odds_used === false ? 'text-rose-300' : 'text-zinc-400'}>{m.same_odds_used === true ? 'yes' : m.same_odds_used === false ? `NO (selector ${m.odds_used} vs trace ${m.ou_trace_odds})` : '—'}</b></span>
                          </>
                        )}
                      </div>
                      {m.block_reason_detail && (
                        <div data-testid={`mea-${market}-detail`} className="mt-1 text-[10px] text-amber-100 italic border-t border-zinc-800/50 pt-1">
                          {m.block_reason_detail}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div data-testid={testid} className="border-t border-zinc-800 pt-2">
      <div className="text-[10px] text-zinc-400 tracking-wider mb-1 font-semibold">
        {title}
      </div>
      {children}
    </div>
  );
}

function KeyValueGrid({ obj, excludeKeys = [] }) {
  if (!obj) return null;
  const entries = Object.entries(obj).filter(([k]) => !excludeKeys.includes(k));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-[10px]">
      {entries.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-2">
          <span className="text-zinc-500">{k}</span>
          <span className="text-zinc-200 font-mono truncate">{fmt(v)}</span>
        </div>
      ))}
    </div>
  );
}
